import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { skeletonRows, withSkeleton } from "@/helpers/tableBody";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { FilterMatchMode } from "primereact/api";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { formatMinutes } from "sj-ui-toolkit/src/utils/time";
import { multiSelectFilterElement } from "@/helpers/tableFilters";
import type { Path, AssignmentPolicyData } from "@/api/assignments";

interface PathSelectionProps {
  availablePaths: Path[];
  selectedPath: Path | null;
  onPathSelect: (path: Path | null) => void;
  showValidationMessages?: boolean;
  validationErrors?: { hasError?: boolean; message?: string };
  isLoading?: boolean;
  existingAssignments?: AssignmentPolicyData[];
  isEditMode?: boolean;
  levelOptions?: Array<{ label: string; value: string }>;
}

const PATH_STATUSES = ["Available", "Archived", "Inactive", "Already Assigned"] as const;

const PATH_STATUS_OPTIONS = PATH_STATUSES.map(s => ({ label: s, value: s }));

const STATUS_COLOR_MAP: Record<string, string> = {
  Available: "text-green-600",
  Archived: "text-gray-400",
  Inactive: "text-orange-500",
  "Already Assigned": "text-blue-400",
};

const SKELETON_ROWS = skeletonRows<Path & { _status: string }>(10);

const getPathStatus = (
  path: Path,
  existingAssignments: AssignmentPolicyData[],
  isEditMode: boolean,
  editedAssignmentPath?: Path | null
): string => {
  if (path.archived) return "Archived";
  if (!path.activated) return "Inactive";
  const alreadyAssigned = existingAssignments.some(
    a => (a as unknown as { roleId?: string }).roleId === path.id
  );
  if (alreadyAssigned && !(isEditMode && editedAssignmentPath?.id === path.id)) {
    return "Already Assigned";
  }
  return "Available";
};

const isPathSelectable = (
  path: Path,
  existingAssignments: AssignmentPolicyData[],
  isEditMode: boolean,
  editedAssignmentPath?: Path | null
): boolean => {
  const status = getPathStatus(path, existingAssignments, isEditMode, editedAssignmentPath);
  return status === "Available" || (isEditMode && editedAssignmentPath?.id === path.id);
};

type PathRow = Path & { _status: string };

const nameBody = withSkeleton<PathRow>(row => row.name ?? "");
const descriptionBody = withSkeleton<PathRow>(row => row.description ?? "");
const typeBody = withSkeleton<PathRow>(row =>
  row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1).replace(/_/g, " ") : ""
);
const levelBody = withSkeleton<PathRow>(row => row.levelName ?? "");
const statusBody = withSkeleton<PathRow>(row => (
  <span className={STATUS_COLOR_MAP[row._status] ?? ""}>{row._status}</span>
));
const lessonsBody = withSkeleton<PathRow>(row => <>{row.lessonsCount ?? ""}</>);
const durationBody = withSkeleton<PathRow>(row =>
  row.totalDuration ? <>{formatMinutes(row.totalDuration)}</> : <>--</>
);

const PathSelection = ({
  availablePaths,
  selectedPath,
  onPathSelect,
  showValidationMessages = false,
  validationErrors,
  isLoading = false,
  existingAssignments = [],
  isEditMode = false,
  levelOptions = [],
}: PathSelectionProps) => {
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[] | null>(
    isEditMode ? null : ["Available"]
  );
  const [levelFilter, setLevelFilter] = useState<string[] | null>(null);

  const filters = useMemo(
    () => ({
      global: { value: globalFilterValue || null, matchMode: FilterMatchMode.CONTAINS },
      _status: { value: statusFilter, matchMode: FilterMatchMode.IN },
      levelName: { value: levelFilter, matchMode: FilterMatchMode.IN },
    }),
    [globalFilterValue, statusFilter, levelFilter]
  );

  const pathsWithStatus = useMemo(
    () =>
      availablePaths.map(p => ({
        ...p,
        _status: getPathStatus(p, existingAssignments, isEditMode, selectedPath),
      })),
    [availablePaths, existingAssignments, isEditMode, selectedPath]
  );

  const hasActiveFilters =
    !!globalFilterValue ||
    (statusFilter !== null && statusFilter.length > 0) ||
    (levelFilter !== null && levelFilter.length > 0);

  const clearFilters = () => {
    setGlobalFilterValue("");
    setStatusFilter(null);
    setLevelFilter(null);
  };

  const availableLevels = levelOptions;

  const levelFilterElement = useMemo(
    () => multiSelectFilterElement(availableLevels, "All Levels"),
    [availableLevels]
  );
  const statusFilterElement = useMemo(
    () => multiSelectFilterElement(PATH_STATUS_OPTIONS, "All Statuses"),
    []
  );

  if (isEditMode && selectedPath) {
    return (
      <div>
        <Card className={themeClasses.bgElevation2}>
          <h4 className="font-semibold body-lg mb-2">Selected Path</h4>
          <div>
            <div className="mb-2">
              <span className="font-semibold">Name: </span>
              {selectedPath.name}
            </div>
            {selectedPath.levelName && (
              <div className="mb-2">
                <span className="font-semibold">Level: </span>
                {selectedPath.levelName}
              </div>
            )}
            {selectedPath.lessonsCount !== undefined && (
              <div className="mb-2">
                <span className="font-semibold">Lessons: </span>
                {selectedPath.lessonsCount}
              </div>
            )}
            {selectedPath.totalDuration && (
              <div>
                <span className="font-semibold">Estimated Duration: </span>
                {formatMinutes(selectedPath.totalDuration)}
              </div>
            )}
          </div>
          <p className="mt-3 body-sm text-secondary">
            Learning path cannot be changed for existing assignments.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {showValidationMessages && validationErrors?.hasError && (
        <div className="mb-4" role="alert">
          <span className="text-red-500">{validationErrors.message}</span>
        </div>
      )}
      <p className="mb-3 text-color-secondary">Select a learning path for this assignment.</p>
      <div className="flex flex-wrap gap-3 mb-3 items-end justify-between">
        <div className="flex items-center gap-2">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={e => setGlobalFilterValue(e.target.value)}
              placeholder="Search by path name"
              aria-label="Search paths by name"
            />
          </IconField>
          {hasActiveFilters && (
            <Button
              label="Clear"
              icon="pi pi-filter-slash"
              size="small"
              severity="secondary"
              onClick={clearFilters}
            />
          )}
        </div>
      </div>
      <DataTable
        aria-label="Available paths"
        value={(isLoading ? SKELETON_ROWS : pathsWithStatus) as unknown as DataTableValueArray}
        selectionMode="single"
        selection={selectedPath ?? undefined}
        onSelectionChange={e => {
          const path = e.value as Path & { _status: string };
          if (path && isPathSelectable(path, existingAssignments, isEditMode, selectedPath)) {
            const { _status: _, ...cleanPath } = path;
            onPathSelect(cleanPath);
          }
        }}
        dataKey={isLoading ? undefined : "id"}
        tableClassName="dt-responsive-text"
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        globalFilterFields={["name", "levelName", "_status"]}
        filters={filters}
        onFilter={e => {
          const f = e.filters as Record<string, { value: unknown; matchMode: FilterMatchMode }>;
          setStatusFilter((f._status?.value as string[] | null) ?? null);
          setLevelFilter((f.levelName?.value as string[] | null) ?? null);
        }}
        filterDisplay="menu"
        emptyMessage="No paths found"
        removableSort
        rowClassName={row => {
          if (!row) return "";
          const r = row as Path & { _status: string };
          return !isPathSelectable(r, existingAssignments, isEditMode, selectedPath)
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer";
        }}
      >
        <Column selectionMode="single" headerStyle={{ width: "3rem" }} />
        <Column field="name" header="Path Name" sortable body={nameBody} className="min-w-36" />
        <Column
          field="description"
          header="Description"
          sortable
          body={descriptionBody}
          className="min-w-64"
        />
        <Column field="type" header="Type" sortable body={typeBody} className="min-w-28" />
        <Column
          field="levelName"
          header="Level"
          sortable
          body={levelBody}
          className="min-w-32"
          filter
          showFilterMatchModes={false}
          filterElement={levelFilterElement}
        />
        <Column
          field="_status"
          header="Status"
          sortable
          body={statusBody}
          className="min-w-36"
          filter
          showFilterMatchModes={false}
          filterElement={statusFilterElement}
        />
        <Column
          field="lessonsCount"
          header="Lessons"
          sortable
          body={lessonsBody}
          className="min-w-24"
        />
        <Column
          field="totalDuration"
          header="Duration"
          sortable
          body={durationBody}
          className="min-w-28"
        />
      </DataTable>
    </div>
  );
};

export default PathSelection;
